import {
  DEFAULT_UI_LOCALE,
  SUPPORTED_UI_LOCALES,
  type SupportedUiLocale,
} from './locales';

const BCP_47_LANGUAGE_TAG_PATTERN = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

const supportedLocalesByCaseFoldedValue = new Map(
  SUPPORTED_UI_LOCALES.map((locale) => [locale.toLocaleLowerCase('en-US'), locale]),
);

export const getBrowserLocale = (): string | null => {
  if (typeof navigator === 'undefined' || typeof navigator.language !== 'string') {
    return null;
  }

  return navigator.language;
};

/**
 * Converts an untrusted locale value to an exact supported locale or a
 * supported base language. The browser never controls catalog selection.
 */
export const normalizeSupportedLocale = (value: unknown): SupportedUiLocale | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const locale = value.trim();
  if (!BCP_47_LANGUAGE_TAG_PATTERN.test(locale)) {
    return null;
  }

  const exactLocale = supportedLocalesByCaseFoldedValue.get(locale.toLocaleLowerCase('en-US'));
  if (exactLocale) {
    return exactLocale;
  }

  const baseLanguage = locale.split('-', 1)[0].toLocaleLowerCase('en-US');
  return supportedLocalesByCaseFoldedValue.get(baseLanguage) ?? null;
};

/**
 * Resolves the effective UI locale from the saved preference, device locale,
 * and finally the deterministic English fallback.
 */
export const resolveEffectiveUiLocale = (
  savedLocale: unknown,
  browserLocale: unknown,
): SupportedUiLocale => (
  normalizeSupportedLocale(savedLocale)
  ?? normalizeSupportedLocale(browserLocale)
  ?? DEFAULT_UI_LOCALE
);